/**
 * Gate 2 metric detail expand: prompt, ground truth, retrieved context,
 * and generated answer only when the metric scores the application reply.
 */
import { Box, Typography } from "@mui/material";
import { unwrapResponseText } from "../../utils/unwrapResponseText";
import {
  catalogIdFromMetricName,
  metricNeedsGeneratedAnswer,
  metricNeedsRetrievedContext,
} from "./implementedRagMetrics";

const FIELD_LABEL_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "16px",
  letterSpacing: 0,
  color: "#707070",
  mb: 0.75,
  display: "block",
};

const FIELD_VALUE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "22px",
  letterSpacing: 0,
  color: "#383838",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const FIELD_PANEL_SX = {
  bgcolor: "#F5F5F5",
  border: "1px solid #E6E6E6",
  borderRadius: "4px",
  boxSizing: "border-box",
  px: 2,
  py: 1.5,
  minHeight: 48,
};

function resolvePrompt(testCase) {
  const direct = String(testCase?.prompt || "").trim();
  if (direct) return direct;
  const input = testCase?.input;
  if (Array.isArray(input)) {
    const first = input.map((p) => String(p || "").trim()).find(Boolean);
    if (first) return first;
  }
  return "";
}

function resolveGroundTruth(testCase) {
  const direct = String(testCase?.expected || "").trim();
  if (direct) return direct;
  const gt = testCase?.groundTruth;
  if (gt) {
    const fromGt = unwrapResponseText(gt.expected_output || gt.expected_behavior || "");
    if (String(fromGt).trim()) return String(fromGt).trim();
  }
  return "";
}

function chunkSourceLabel(chunk, index) {
  const name =
    chunk?.title ||
    chunk?.documentName ||
    chunk?.fileName ||
    chunk?.source ||
    chunk?.documentId ||
    chunk?.document_id ||
    "Retrieved source";
  return `#${index + 1}. ${name}`;
}

function retrievedContextCards(testCase) {
  const chunks = Array.isArray(testCase?.chunks) ? testCase.chunks : [];
  return chunks
    .map((chunk, i) => {
      const content = String(chunk?.content || chunk?.text || chunk?.pageContent || "").trim();
      if (!content) return null;
      return {
        key: `chunk-${chunk?.id ?? i}`,
        header: chunkSourceLabel(chunk, i),
        content,
      };
    })
    .filter(Boolean);
}

function resolveGeneratedAnswer(testCase) {
  return String(
    testCase?.answer || testCase?.actual_output || testCase?.evidence?.actualOutput || ""
  ).trim();
}

function resolveCatalogMetricId(catalogMetricId, testCase) {
  return (
    String(catalogMetricId || "").trim() ||
    String(testCase?.catalogMetricId || testCase?.metric_id || "").trim() ||
    catalogIdFromMetricName(testCase?.metricName) ||
    ""
  );
}

function EvidenceOutcomeCard({ header, content }) {
  return (
    <Box
      sx={{
        border: "1px solid #E6E6E6",
        borderRadius: "4px",
        overflow: "hidden",
        bgcolor: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "#EFF6FF",
          borderBottom: "1px solid #BFDBFE",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 13,
            fontWeight: 700,
            lineHeight: "20px",
            color: "#005BF0",
          }}
        >
          {header}
        </Typography>
      </Box>
      <Box
        sx={{
          borderLeft: "3px solid #005BF0",
          bgcolor: "#FFFFFF",
          px: 2,
          py: 1.5,
          ml: 0,
        }}
      >
        <Typography sx={FIELD_VALUE_SX}>{content}</Typography>
      </Box>
    </Box>
  );
}

export default function Gate2TestCaseEvidenceExpandPanel({ testCase, row, catalogMetricId }) {
  const prompt = resolvePrompt(testCase);
  const groundTruth = resolveGroundTruth(testCase);
  const metricId = resolveCatalogMetricId(catalogMetricId, testCase);
  const showRetrieved = metricNeedsRetrievedContext(metricId);
  const showGeneratedAnswer = metricNeedsGeneratedAnswer(metricId);
  const contextCards = retrievedContextCards(testCase);
  const execError = String(testCase?.error?.message || "").trim();
  const capturedAnswer = resolveGeneratedAnswer(testCase);
  const generatedAnswer = capturedAnswer && capturedAnswer !== execError ? capturedAnswer : "";
  const reason = row?.reason ? String(row.reason).trim() : "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        px: { xs: 1, sm: 2 },
        py: 2,
        bgcolor: "#FFFFFF",
        boxSizing: "border-box",
      }}
    >
      <Box>
        <Typography component="span" sx={FIELD_LABEL_SX}>
          Prompt Sent
        </Typography>
        <Box sx={FIELD_PANEL_SX}>
          {prompt ? (
            <Typography sx={FIELD_VALUE_SX}>&ldquo;{prompt}&rdquo;</Typography>
          ) : (
            <Typography sx={{ ...FIELD_VALUE_SX, color: "#98A2B3", fontStyle: "italic" }}>
              No prompt recorded.
            </Typography>
          )}
        </Box>
      </Box>

      <Box>
        <Typography component="span" sx={FIELD_LABEL_SX}>
          Ground Truth
        </Typography>
        <Box sx={FIELD_PANEL_SX}>
          {groundTruth ? (
            <Typography sx={FIELD_VALUE_SX}>{groundTruth}</Typography>
          ) : (
            <Typography sx={{ ...FIELD_VALUE_SX, color: "#98A2B3", fontStyle: "italic" }}>
              No ground truth recorded.
            </Typography>
          )}
        </Box>
      </Box>

      {showRetrieved || contextCards.length ? (
        <Box>
          <Typography component="span" sx={FIELD_LABEL_SX}>
            Retrieved context ({contextCards.length})
          </Typography>
          {contextCards.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 0.25 }}>
              {contextCards.map((item) => (
                <EvidenceOutcomeCard key={item.key} header={item.header} content={item.content} />
              ))}
            </Box>
          ) : (
            <Box sx={FIELD_PANEL_SX}>
              <Typography sx={{ ...FIELD_VALUE_SX, color: "#98A2B3", fontStyle: "italic" }}>
                No vector documents were retrieved for this prompt.
              </Typography>
            </Box>
          )}
        </Box>
      ) : null}

      {showGeneratedAnswer ? (
        <Box>
          <Typography component="span" sx={FIELD_LABEL_SX}>
            Generated answer from the application
          </Typography>
          <Box sx={FIELD_PANEL_SX}>
            {generatedAnswer ? (
              <Typography sx={FIELD_VALUE_SX}>{generatedAnswer}</Typography>
            ) : (
              <Typography sx={{ ...FIELD_VALUE_SX, color: "#98A2B3", fontStyle: "italic" }}>
                No application-generated answer was captured for this metric.
              </Typography>
            )}
          </Box>
        </Box>
      ) : null}

      {execError ? (
        <Box>
          <Typography component="span" sx={FIELD_LABEL_SX}>
            Execution error
          </Typography>
          <Box sx={FIELD_PANEL_SX}>
            <Typography sx={FIELD_VALUE_SX}>{execError}</Typography>
          </Box>
        </Box>
      ) : null}

      {reason ? (
        <Box>
          <Typography component="span" sx={FIELD_LABEL_SX}>
            Judge reason
          </Typography>
          <Box sx={FIELD_PANEL_SX}>
            <Typography sx={FIELD_VALUE_SX}>{reason}</Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
